import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { DatePickerContext, useDatePickerContext } from './DatePickerContext';
import { useDatePicker } from './useDatePicker';
import { useDatePickerPosition } from './useDatePickerPosition';
import type {
  DatePickerActionsProps,
  DatePickerGridProps,
  DatePickerHeaderProps,
  DatePickerModalProps,
  DatePickerRootProps,
  DatePickerYearPickerProps,
  DayState,
} from './DatePickerModal.types';

// ── Design tokens ─────────────────────────────────────────────────────────────
// Centralised colour references — override via className props as needed.
const DP = {
  surface: '#ECEEEF',
  text: '#181A1B',
  textMuted: '#474A4C',
  primary: '#2A4455',
  range: '#FFF2B3',
  shadow: '0px_1px_3px_0px_rgba(0,0,0,0.15),0px_4px_8px_3px_rgba(0,0,0,0.08)',
} as const;

// ── Root ──────────────────────────────────────────────────────────────────────

export function DatePickerRoot({
  isOpen,
  onClose,
  onSelect,
  onRangeSelect,
  selected,
  startDate,
  endDate,
  mode = 'single',
  anchorEl,
  minDate,
  maxDate,
  startYear,
  endYear,
  placement = 'bottom-start',
  compact = false,
  children,
  className,
}: DatePickerRootProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  const { positionStyle, contentStyle } = useDatePickerPosition({
    isOpen,
    anchorEl,
    placement,
    compact,
    modalRef,
  });

  const state = useDatePicker({
    isOpen,
    mode,
    selected,
    startDate,
    endDate,
    minDate,
    maxDate,
    startYear,
    endYear,
    onSelect,
    onRangeSelect,
    onClose,
  });

  // Click-outside to close (exclude the anchor element itself)
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (modalRef.current?.contains(target) || anchorEl?.contains(target)) {
        return;
      }
      onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen, onClose, anchorEl]);

  if (!isOpen) {
    return null;
  }

  const gap = compact ? 'gap-2' : 'gap-6';
  const padding = compact ? 'p-2' : 'p-5';

  return (
    <DatePickerContext.Provider value={{ ...state, compact, mode }}>
      {/* Backdrop — only when no anchor (centred dialog mode) */}
      {!anchorEl && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-40"
          onClick={state.handleCancel}
        />
      )}

      <style>{`
        @media (max-height: 720px) {
          .date-picker-dropdown-content {
            padding: 8px 12px !important;
            gap: 4px !important;
            border-radius: 12px !important;
          }
          .date-picker-header {
            margin-bottom: 4px !important;
          }
          .date-picker-header-label {
            font-size: 11px !important;
            line-height: 14px !important;
          }
          .date-picker-header-display {
            font-size: 15px !important;
            line-height: 20px !important;
            margin-top: 4px !important;
          }
          .date-picker-nav {
             margin-top: 2px !important;
             margin-bottom: 0 !important;
          }
          .date-picker-nav button:not([aria-expanded]) {
            width: 32px !important;
            height: 32px !important;
          }
          .date-picker-nav h2 {
            font-size: 13px !important;
          }
          .date-picker-grid-container {
            margin-bottom: 0 !important;
          }
          .date-picker-grid-container table th {
            width: 32px !important;
            height: 24px !important;
          }
          .date-picker-grid-container table td {
            width: 32px !important;
            height: 32px !important;
          }
          .date-picker-grid-container table td button,
          .date-picker-grid-container table td span {
            width: 28px !important;
            height: 28px !important;
            font-size: 11px !important;
          }
          .date-picker-actions {
            height: 40px !important;
            padding-top: 0 !important;
            margin-top: 4px !important;
          }
          .date-picker-actions button {
            height: 36px !important;
            padding: 0 12px !important;
          }
          .date-picker-actions button span {
            font-size: 13px !important;
          }
        }
      `}</style>
      {/* Outer: position:fixed shell rendered into document.body via portal so it
           always sits in the root stacking context and is never clipped or
           obscured by an ancestor's stacking context (e.g. a z-indexed parent
           like FilterPopup). */}
      {createPortal(
        <div
          style={positionStyle}
          className="z-50"
          data-date-picker-portal="true"
        >
          {/* Inner: content with optional scale transform for zoom-level correction */}
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={state.headingId}
            id={state.dialogId}
            style={contentStyle}
            className={`date-picker-dropdown-content dropdown-content bg-[${DP.surface}] rounded-[20px] shadow-[${DP.shadow}] ${padding} flex flex-col ${gap} overflow-hidden min-h-0${className ? ` ${className}` : ''}`}
          >
            {children}
          </div>
        </div>,
        document.body
      )}
    </DatePickerContext.Provider>
  );
}

// ── Header ────────────────────────────────────────────────────────────────────

export function DatePickerHeader({
  label = 'Wybierz datę',
  className,
}: DatePickerHeaderProps) {
  const {
    headingId,
    gridId,
    monthLabel,
    showYearPicker,
    canGoToPrevMonth,
    canGoToNextMonth,
    goToPrevMonth,
    goToNextMonth,
    toggleYearPicker,
    tempSelected,
    tempRange,
    compact,
    mode,
  } = useDatePickerContext();

  const rangeDisplay =
    mode === 'range'
      ? `${tempRange.start ? format(tempRange.start, 'yyyy/MM/dd', { locale: pl }) : 'Data początkowa'} - ${tempRange.end ? format(tempRange.end, 'yyyy/MM/dd', { locale: pl }) : 'Data końcowa'}`
      : null;

  const singleDisplay =
    mode === 'single' && tempSelected
      ? format(tempSelected, 'EEE, MMM d', { locale: pl })
      : null;

  const mb = compact ? 'mb-1' : 'mb-4';
  const mt = compact ? 'mt-4' : 'mt-6';

  return (
    <div
      className={`date-picker-header select-none shrink-0 ${mb}${className ? ` ${className}` : ''}`}
    >
      {/* Label */}
      <div
        className={`date-picker-header-label text-[${DP.textMuted}] text-xs font-medium leading-4 tracking-[0.5px]`}
      >
        {label}
      </div>

      {/* Selected display */}
      {rangeDisplay && (
        <div
          className={`date-picker-header-display text-[${DP.text}] text-lg leading-[26px] ${mt}`}
        >
          {rangeDisplay}
        </div>
      )}
      {singleDisplay && (
        <div
          className={`date-picker-header-display text-[${DP.text}] text-lg leading-[26px] ${mt}`}
        >
          {singleDisplay}
        </div>
      )}

      {/* Month navigation row */}
      <div className="date-picker-nav flex justify-between items-center shrink-0 mt-2 mb-1">
        {/* Year picker toggle */}
        <button
          type="button"
          aria-expanded={showYearPicker}
          aria-controls={gridId}
          onClick={toggleYearPicker}
          className="flex w-fit shrink-0 items-center gap-2 py-2.5 rounded-full hover:bg-black/5 cursor-pointer"
        >
          <h2
            id={headingId}
            className={`text-[${DP.textMuted}] text-sm font-medium leading-5 tracking-[0.1px] whitespace-nowrap`}
          >
            {`${monthLabel}`}
          </h2>
          <svg
            aria-hidden="true"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          >
            <path d="M9 11.25L5.25 7.5H12.75L9 11.25Z" fill={DP.textMuted} />
          </svg>
        </button>

        {/* Prev / next month */}
        <div className="flex">
          <button
            type="button"
            aria-label="Poprzedni miesiąc"
            aria-disabled={!canGoToPrevMonth}
            disabled={!canGoToPrevMonth}
            data-testid="date-picker-prev-month"
            onClick={goToPrevMonth}
            className="flex items-center justify-center w-12 h-12 rounded-full hover:bg-black/5 disabled:opacity-38 disabled:cursor-not-allowed cursor-pointer"
          >
            <svg
              aria-hidden="true"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M14 18L8 12L14 6L15.4 7.4L10.8 12L15.4 16.6L14 18Z"
                fill={DP.textMuted}
              />
            </svg>
          </button>
          <button
            type="button"
            aria-label="Następny miesiąc"
            aria-disabled={!canGoToNextMonth}
            disabled={!canGoToNextMonth}
            data-testid="date-picker-next-month"
            onClick={goToNextMonth}
            className="flex items-center justify-center w-12 h-12 rounded-full hover:bg-black/5 disabled:opacity-38 disabled:cursor-not-allowed cursor-pointer"
          >
            <svg
              aria-hidden="true"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12.6 12L8 7.4L9.4 6L15.4 12L9.4 18L8 16.6L12.6 12Z"
                fill={DP.textMuted}
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Grid ──────────────────────────────────────────────────────────────────────

export function DatePickerGrid({
  className,
  cellClassName,
}: DatePickerGridProps) {
  const {
    showYearPicker,
    headingId,
    gridId,
    calendarDays,
    weekDayLabels,
    focusedDate,
    getDayState,
    handleDayClick,
    handleGridKeyDown,
    compact,
  } = useDatePickerContext();

  const gridRef = useRef<HTMLTableElement>(null);

  // Roving tabindex: focus the active date button whenever focusedDate changes
  useEffect(() => {
    if (!focusedDate || !gridRef.current) {
      return;
    }
    const dateStr = format(focusedDate, 'yyyy-MM-dd');
    const btn = gridRef.current.querySelector<HTMLButtonElement>(
      `[data-date="${dateStr}"]`
    );
    btn?.focus({ preventScroll: true });
  }, [focusedDate]);

  if (showYearPicker) {
    return null;
  }

  // Split flat day array into week rows
  const weeks: Date[][] = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7) as Date[]);
  }

  const mb = compact ? 'mb-0' : 'mb-2';

  const resolveCellCn = (state: DayState): string => {
    if (!cellClassName) {
      return '';
    }
    return typeof cellClassName === 'function'
      ? cellClassName(state)
      : cellClassName;
  };

  return (
    <div
      className={`date-picker-grid-container flex-1 min-h-0 overflow-hidden ${mb}${className ? ` ${className}` : ''}`}
    >
      <table
        ref={gridRef}
        role="grid"
        id={gridId}
        aria-labelledby={headingId}
        className="w-full border-collapse"
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        onKeyDown={handleGridKeyDown}
      >
        {/* Week-day column headers */}
        <thead style={{ display: 'block', flexShrink: 0 }}>
          <tr
            role="row"
            style={{ display: 'table', width: '100%', tableLayout: 'fixed' }}
          >
            {weekDayLabels.map((day, i) => (
              <th
                key={i}
                role="columnheader"
                abbr={day}
                scope="col"
                className="w-9 h-8 text-center"
              >
                <span
                  className={`text-[${DP.text}] text-xs leading-4 tracking-[0.4px]`}
                >
                  {day}
                </span>
              </th>
            ))}
          </tr>
        </thead>

        {/* Day cells */}
        <tbody
          style={{ display: 'block', overflowY: 'auto', flex: 1, minHeight: 0 }}
        >
          {weeks.map((week, wi) => (
            <tr
              key={wi}
              role="row"
              style={{ display: 'table', width: '100%', tableLayout: 'fixed' }}
            >
              {week.map((day, di) => {
                const state = getDayState(day);
                const dateStr = format(day, 'yyyy-MM-dd');
                const ariaLabel = format(day, 'd MMMM yyyy', { locale: pl });

                // Range highlight on the TD — only days strictly between start
                // and end get the yellow fill; start/end cells show no fill.
                // Use inline style instead of dynamic Tailwind class so the
                // colour is always applied regardless of build-time purging.
                const cellStyle = state.isInRange
                  ? { backgroundColor: DP.range }
                  : undefined;

                // Day button base style
                let btnStyle = '';
                if (state.isSelected) {
                  btnStyle = `bg-[${DP.primary}] text-white`;
                } else if (state.isToday) {
                  btnStyle = `outline outline-[${DP.primary}] -outline-offset-1 text-[${DP.primary}]`;
                } else {
                  btnStyle = `text-[${DP.text}]`;
                }

                const extraCn = resolveCellCn(state);

                return (
                  <td
                    key={di}
                    role="gridcell"
                    aria-selected={state.isSelected || undefined}
                    aria-disabled={state.isDisabled || undefined}
                    style={cellStyle}
                    className="w-9 h-9 p-0 relative"
                  >
                    <div className="flex items-center justify-center w-9 h-9">
                      {state.isCurrentMonth ? (
                        <button
                          type="button"
                          data-date={dateStr}
                          data-selected={state.isSelected || undefined}
                          data-today={state.isToday || undefined}
                          data-in-range={state.isInRange || undefined}
                          data-range-start={state.isRangeStart || undefined}
                          data-range-end={state.isRangeEnd || undefined}
                          data-disabled={state.isDisabled || undefined}
                          aria-label={ariaLabel}
                          aria-current={state.isToday ? 'date' : undefined}
                          aria-pressed={state.isSelected}
                          tabIndex={state.isFocused ? 0 : -1}
                          disabled={state.isDisabled}
                          onClick={() => handleDayClick(day)}
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs leading-4 tracking-[0.4px] z-10 ${btnStyle} ${state.isDisabled ? 'opacity-38 cursor-not-allowed' : `cursor-pointer${!state.isSelected ? ' hover:bg-black/5' : ''}`}${extraCn ? ` ${extraCn}` : ''}`}
                        >
                          {format(day, 'd')}
                        </button>
                      ) : (
                        <span
                          aria-hidden="true"
                          data-outside-month="true"
                          className={`w-8 h-8 flex items-center justify-center text-xs leading-4 tracking-[0.4px] text-[${DP.text}] opacity-38`}
                        >
                          {format(day, 'd')}
                        </span>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Year picker ───────────────────────────────────────────────────────────────

export function DatePickerYearPicker({
  className,
  yearButtonClassName,
}: DatePickerYearPickerProps) {
  const { showYearPicker, years, currentMonth, selectYear, compact } =
    useDatePickerContext();

  const yearPickerRef = useRef<HTMLDivElement>(null);
  const currentYear = currentMonth.getFullYear();

  // Scroll the current year into view when the picker opens
  useEffect(() => {
    if (!showYearPicker || !yearPickerRef.current) {
      return;
    }
    const btn = yearPickerRef.current.querySelector<HTMLElement>(
      `[data-year="${currentYear}"]`
    );
    btn?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [showYearPicker, currentYear]);

  if (!showYearPicker) {
    return null;
  }

  const mb = compact ? 'mb-0' : 'mb-2';
  const maxH = compact ? 'max-h-[200px]' : 'max-h-[240px]';

  const resolveYearCn = (year: number): string => {
    if (!yearButtonClassName) {
      return '';
    }
    return typeof yearButtonClassName === 'function'
      ? yearButtonClassName(year, year === currentYear)
      : yearButtonClassName;
  };

  return (
    <div
      ref={yearPickerRef}
      role="listbox"
      aria-label="Wybierz rok"
      className={`flex-1 min-h-0 ${maxH} px-2 overflow-y-auto overflow-x-hidden scroll-smooth ${mb}${className ? ` ${className}` : ''}`}
      style={{ scrollbarWidth: 'thin' }}
    >
      <div className="grid grid-cols-3 gap-2 py-2">
        {years.map((year) => {
          const extraCn = resolveYearCn(year);
          return (
            <button
              key={year}
              type="button"
              role="option"
              data-year={year}
              aria-selected={year === currentYear}
              onClick={() => selectYear(year)}
              className={`h-10 rounded-lg flex items-center justify-center text-sm font-medium cursor-pointer transition-colors ${year === currentYear ? `bg-[${DP.primary}] text-white` : `text-[${DP.textMuted}] hover:bg-black/5`}${extraCn ? ` ${extraCn}` : ''}`}
            >
              {year}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Actions ───────────────────────────────────────────────────────────────────

export function DatePickerActions({
  cancelLabel = 'Anuluj',
  confirmLabel = 'OK',
  className,
}: DatePickerActionsProps) {
  const { handleOk, handleCancel, compact } = useDatePickerContext();
  const pt = compact ? 'pt-0' : 'pt-2';

  return (
    <div
      className={`date-picker-actions flex justify-end gap-2 shrink-0 ${pt}${className ? ` ${className}` : ''}`}
    >
      <button
        type="button"
        onClick={handleCancel}
        className="h-12 px-4 py-2.5 rounded-full hover:bg-black/5 cursor-pointer"
      >
        <span
          className={`text-[${DP.primary}] text-sm font-medium leading-5 tracking-[0.1px]`}
        >
          {cancelLabel}
        </span>
      </button>
      <button
        type="button"
        onClick={handleOk}
        className="h-12 px-4 py-2.5 rounded-full hover:bg-black/5 cursor-pointer"
      >
        <span
          className={`text-[${DP.primary}] text-sm font-medium leading-5 tracking-[0.1px]`}
        >
          {confirmLabel}
        </span>
      </button>
    </div>
  );
}

// ── Default composition (backward-compatible) ─────────────────────────────────
// Preserves the original flat-props API so existing usage sites need no changes.

export function DatePickerModal({
  label,
  cancelLabel,
  confirmLabel,
  ...rootProps
}: DatePickerModalProps) {
  return (
    <DatePickerRoot {...rootProps}>
      {/* Scrollable content area */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <DatePickerHeader label={label} />
        <DatePickerGrid />
        <DatePickerYearPicker />
      </div>
      <DatePickerActions
        cancelLabel={cancelLabel}
        confirmLabel={confirmLabel}
      />
    </DatePickerRoot>
  );
}

// ── Compound surface ──────────────────────────────────────────────────────────

DatePickerModal.Root = DatePickerRoot;
DatePickerModal.Header = DatePickerHeader;
DatePickerModal.Grid = DatePickerGrid;
DatePickerModal.YearPicker = DatePickerYearPicker;
DatePickerModal.Actions = DatePickerActions;
